import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, Signal } from "@angular/core";
import { TranslationService } from "./i18n";
// language list
import { locale as enLang } from "./i18n/vocabs/en";
import { locale as thLang } from "./i18n/vocabs/th";
import { ThemeModeService } from "./_metronic/partials/layout/theme-mode-switcher/theme-mode.service";
import { LayoutService } from "./_metronic/layout";
import {
  LayoutType,
  ThemeLayout,
} from "./_metronic/layout/core/configs/config";
import { CommonModule } from "@angular/common";
import { RouterOutlet } from "@angular/router";
import { SharedModule } from "src/shared/shared.module";
import { AuthKkuWorkloadSync } from "./pages/auth/models/user.model";
import { AuthService } from "./pages/auth/services/auth.service";
import { Subscription, timer } from "rxjs";
import { switchMap } from "rxjs/operators";

const ACTIVE_SYNC_VERIFY_POLL_INTERVAL_MS = 5000;
const SESSION_VERIFY_IDLE_POLL_INTERVAL_MS = 30000;

@Component({
   // eslint-disable-next-line @angular-eslint/component-selector
  selector: "body[root]",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, RouterOutlet, SharedModule],
})
export class AppComponent implements OnInit, OnDestroy {
  ThemeLayout = ThemeLayout;
  baseLayout: LayoutType = ThemeLayout.LIGHT_SIDEBAR;

  private authService = inject(AuthService);
  private translationService = inject(TranslationService);
  private modeService = inject(ThemeModeService);
  private layoutService = inject(LayoutService);
  private authSessionSubscription?: Subscription;
  private globalSyncPollingSubscription?: Subscription;
  private globalSyncPollingIntervalMs?: number;

  constructor() {
    // register translations
    this.translationService.loadTranslations(enLang, thLang);
  }

  get activeKkuWorkloadSync(): Signal<AuthKkuWorkloadSync | null> {
    return this.authService.activeKkuWorkloadSync;
  }

  ngOnInit() {
    this.translationService.setLanguage("th");
    this.layoutService.setBaseLayoutType(this.baseLayout);
    this.modeService.init();

    setTimeout(() => {
      const splash = document.getElementById("splash-screen");
      if (splash) {
        splash.classList.add("fade-out");
        setTimeout(() => splash.remove(), 300);
      }
    }, 500);

    this.authSessionSubscription = this.authService.currentUser$.subscribe(() => {
      this.syncGlobalSyncPollingState();
    });
    this.syncGlobalSyncPollingState();
  }

  ngOnDestroy(): void {
    this.authSessionSubscription?.unsubscribe();
    this.stopGlobalSyncPolling();
  }

  private syncGlobalSyncPollingState(): void {
    if (this.authService.currentUserValue == null && this.authService.currentUser() == null) {
      this.stopGlobalSyncPolling();
      return;
    }

    const intervalMs = this.activeKkuWorkloadSync() == null
      ? SESSION_VERIFY_IDLE_POLL_INTERVAL_MS
      : ACTIVE_SYNC_VERIFY_POLL_INTERVAL_MS;

    if (
      this.globalSyncPollingSubscription != null &&
      this.globalSyncPollingIntervalMs === intervalMs
    ) {
      return;
    }

    this.stopGlobalSyncPolling();
    this.globalSyncPollingIntervalMs = intervalMs;
    this.globalSyncPollingSubscription = timer(
      intervalMs,
      intervalMs,
    ).pipe(
      switchMap(() => this.authService.restoreSession()),
    ).subscribe(() => {
      this.syncGlobalSyncPollingState();
    });
  }

  private stopGlobalSyncPolling(): void {
    this.globalSyncPollingSubscription?.unsubscribe();
    this.globalSyncPollingSubscription = undefined;
    this.globalSyncPollingIntervalMs = undefined;
  }
}
